import { cn } from '@/utils/helpers';

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeStyles = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

export function Spinner({ size = 'md', className }: SpinnerProps) {
  return (
    <svg
      className={cn('animate-spin text-primary-600', sizeStyles[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

interface LoadingOverlayProps {
  message?: string;
}

export function LoadingOverlay({ message = 'Loading...' }: LoadingOverlayProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-950">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-orange-500/[0.04] dark:bg-orange-500/[0.06] rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center">
        {/* DayDrive Logo */}
        <div className="relative mb-6">
          <div className="w-16 h-16 rounded-2xl bg-orange-50 dark:bg-gray-900/80 border border-orange-100 dark:border-gray-800/50 flex items-center justify-center">
            <svg width={36} height={36} viewBox="30 50 300 330" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lo-top" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FFEEE4"/><stop offset="100%" stopColor="#FFE4D6"/>
                </linearGradient>
                <linearGradient id="lo-left" x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FDBA74"/><stop offset="100%" stopColor="#FB923C"/>
                </linearGradient>
                <linearGradient id="lo-right" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F97316"/><stop offset="100%" stopColor="#EA580C"/>
                </linearGradient>
              </defs>
              <g><path d="M40 250 L40 340 L100 370 L100 280 Z" fill="url(#lo-left)"/><path d="M100 280 L100 370 L160 340 L160 250 Z" fill="url(#lo-right)"/><path d="M40 250 L100 220 L160 250 L100 280 Z" fill="url(#lo-top)"/></g>
              <g><path d="M120 180 L120 340 L180 370 L180 210 Z" fill="url(#lo-left)"/><path d="M180 210 L180 370 L240 340 L240 180 Z" fill="url(#lo-right)"/><path d="M120 180 L180 150 L240 180 L180 210 Z" fill="url(#lo-top)"/></g>
              <g><path d="M200 90 L200 340 L260 370 L260 120 Z" fill="url(#lo-left)"/><path d="M260 120 L260 370 L320 340 L320 90 Z" fill="url(#lo-right)"/><path d="M200 90 L260 60 L320 90 L260 120 Z" fill="url(#lo-top)"/></g>
            </svg>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-40 h-0.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden mb-5">
          <div
            className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full animate-pulse"
            style={{ width: '60%' }}
          />
        </div>

        <p className="text-gray-900 dark:text-white text-sm font-medium tracking-tight">{message}</p>
      </div>
    </div>
  );
}
